import { buttonVariants } from "@/components/ui/button";
import { useIsThinking, useIsThinkingDots } from "@/hooks";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { cn } from "@/lib/utils";
import { useMessageStore } from "@/state/stores";
import { Link, useMatches, useParams, useSearch } from "@tanstack/react-router";
import { Eye, File } from "lucide-react";
import type { FpFile } from "../../../../../schemas/codegen";

interface MarkdownAttachmentProps {
  attachment: FpFile;
}

export function MarkdownAttachment({ attachment }: MarkdownAttachmentProps) {
  const chatId = useActiveChatId();
  const messages = useMessageStore((state) => state.messages);
  const matches = useMatches();
  const isOnAttachmentsRoute = matches.some(
    (match) =>
      match.routeId === "/(codegen)/codegen/$chatId/attachments/$versionNumber",
  );

  let currentTab = "spec";
  if (isOnAttachmentsRoute) {
    const search = useSearch({
      from: "/(codegen)/codegen/$chatId/attachments/$versionNumber",
    });
    currentTab = search.tab;
  }

  const isViewingSpec = currentTab === "spec";
  const urlVersionNumber =
    useParams({
      strict: false,
      select: (params) => params.versionNumber,
    }) ?? "v1";

  // This shouldn't happen, but is here to satisfy the type checker
  if (!chatId) {
    throw new Error("No chat ID available");
  }

  const { agentIsThinking } = useIsThinking();

  // Find the message that contains this spec
  const messageWithSpec = messages.find(
    (message) => message.spec?.id === attachment.id,
  );

  // Try to get version number if available
  const versionNumber = messageWithSpec?.version?.version ?? 1;

  // Choose the appropriate route
  const linkProps = {
    to: "/codegen/$chatId/attachments/$versionNumber",
    params: { chatId, versionNumber: `v${versionNumber}` },
    search: { tab: "spec" },
  };

  return (
    <div className="bg-input px-4 py-2 rounded-lg flex gap-3 items-center">
      <div className="inline-grid grid-cols-[auto_1fr] items-center gap-2 text-sm bg-background leading-none px-2 h-7 rounded-sm font-mono">
        <File className="h-4 w-4" />
        <span>{attachment.filename || "Attachment"}</span>
      </div>

      <Link
        {...linkProps}
        className={cn(
          buttonVariants({ variant: "secondary", size: "sm" }),
          "border",
        )}
      >
        <Eye className="h-4 w-4" />
        {isViewingSpec && urlVersionNumber === `v${versionNumber}`
          ? "Viewing"
          : "View"}
      </Link>

      {attachment.status === "processing" && agentIsThinking && (
        <GeneratingDots />
      )}
    </div>
  );
}

function GeneratingDots() {
  const dots = useIsThinkingDots();

  return (
    <span className="text-sm text-muted-foreground">Generating{dots}</span>
  );
}
